import { useState, useEffect } from "react";
import { FiUser, FiPhone, FiBook, FiHome, FiCreditCard, FiAlertCircle } from "react-icons/fi";
import portalService from "../../../services/portalService";

const extract = (r) => r?.data ?? r;

function Section({ title, icon: Icon, children }) {
    return (
        <div className="card">
            <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <Icon size={13} /> {title}
            </div>
            <div className="card-body">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.85rem" }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function Field({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", fontWeight: 700, marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{value}</div>
        </div>
    );
}

export default function StudentProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        portalService.getProfile()
            .then((r) => {
                const data = extract(r);
                if (data?._id || data?.enrollmentNo) setProfile(data);
                else setError("Could not load profile data.");
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading profile…</div>;

    if (error) return (
        <div>
            <div className="page-header"><h1 className="page-title">My Profile</h1></div>
            <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "1.25rem", display: "flex", gap: "0.75rem" }}>
                <FiAlertCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                <div>
                    <div style={{ fontWeight: 700, color: "#dc2626" }}>Could not load profile</div>
                    <div style={{ fontSize: 13, fontFamily: "monospace", marginTop: 4 }}>{error}</div>
                </div>
            </div>
        </div>
    );

    if (!profile) return null;

    const initials = profile.fullName?.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "ST";

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Profile</h1>
                    <p className="page-subtitle">{profile.course?.shortName || "Course"} {profile.yearNumber ? `· Year ${profile.yearNumber}` : ""}</p>
                </div>
            </div>

            {/* Profile card */}
            <div className="card" style={{ marginBottom: "1rem", padding: "1.5rem" }}>
                <div style={{ display: "flex", gap: "1.25rem", alignItems: "center" }}>
                    <div style={{ width: 72, height: 72, borderRadius: 18, overflow: "hidden", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: "#475569", flexShrink: 0 }}>
                        {profile.studentPhoto?.url
                            ? <img src={profile.studentPhoto.url} alt={profile.fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : initials}
                    </div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>{profile.fullName}</div>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: 6 }}>
                            <span className="badge badge-blue">{profile.enrollmentNo}</span>
                            <span style={{ background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{profile.status}</span>
                            {profile.admissionCategory && (
                                <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>{profile.admissionCategory}</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gap: "1rem" }}>
                <Section title="Academic Details" icon={FiBook}>
                    <Field label="Course" value={profile.course?.shortName || profile.course?.courseName} />
                    <Field label="Year" value={profile.yearNumber ? `${profile.yearNumber}${profile.yearNumber === 1 ? "st" : profile.yearNumber === 2 ? "nd" : profile.yearNumber === 3 ? "rd" : "th"} Year` : "—"} />
                    <Field label="Roll No." value={profile.rollNo} />
                    <Field label="Admission Type" value={profile.admissionType} />
                    <Field label="Student Type" value={profile.studentType} />
                    <Field label="Admission Date" value={profile.currentAdmissionDate} />
                </Section>

                <Section title="Personal Details" icon={FiUser}>
                    <Field label="Date of Birth" value={profile.dob} />
                    <Field label="Gender" value={profile.gender} />
                    <Field label="Blood Group" value={profile.bloodGroup} />
                    <Field label="Nationality" value={profile.nationality} />
                    <Field label="Religion" value={profile.religion} />
                    <Field label="Mother Tongue" value={profile.motherTongue} />
                    <Field label="Aadhaar No." value={profile.aadharNo} />
                    <Field label="APAAR ID" value={profile.aaparId} />
                </Section>

                <Section title="Contact Details" icon={FiPhone}>
                    <Field label="Mobile" value={profile.mobile} />
                    <Field label="Email" value={profile.email} />
                    <Field label="Father's Mobile" value={profile.fatherMobile} />
                    <Field label="Parent Email" value={profile.parentEmailAddress} />
                </Section>

                <Section title="Family Details" icon={FiUser}>
                    <Field label="Father's Name" value={profile.fatherName} />
                    <Field label="Mother's Name" value={profile.motherName} />
                    <Field label="Father's Occ." value={profile.parentOccupation} />
                    <Field label="Mother's Occ." value={profile.motherOccupation} />
                </Section>

                <Section title="Address" icon={FiHome}>
                    <Field label="Address" value={profile.address} />
                    <Field label="City" value={profile.city} />
                    <Field label="District" value={profile.district} />
                    <Field label="State" value={profile.state} />
                    <Field label="Pincode" value={profile.pincode} />
                </Section>

                <Section title="Bank Details" icon={FiCreditCard}>
                    <Field label="Bank Account No." value={profile.bankAccountNo} />
                    <Field label="Bank Name" value={profile.bankName} />
                    <Field label="Branch" value={profile.branchName} />
                    <Field label="IFSC Code" value={profile.ifscCode} />
                </Section>
            </div>
        </div>
    );
}